import { useState, useEffect } from 'react';
import { Link2, CheckCircle, XCircle, AlertCircle, Loader2, Building2, RefreshCw, GraduationCap } from 'lucide-react';
import { getUser, authenticatedFetch } from '../lib/auth';

interface MemberIntegration {
  id: string;
  member_id: string;
  member_name: string;
  has_api_key: boolean;
  enabled_by: string | null;
  enabled_at: string | null;
  agreement_confirmed: boolean;
  agreement_confirmed_at: string | null;
  quality_control_confirmed: boolean;
  content_alignment_confirmed: boolean;
  assessment_guidelines_confirmed: boolean;
  spot_check_allowed: boolean;
  sync_enabled: boolean;
  last_sync_at: string | null;
  last_sync_status: string | null;
  total_synced_completions: number;
  total_certificates_issued: number;
  mapping_count: number;
  approved_by_forbund: boolean;
}

interface MoodleIntegration {
  id: string;
  organization_id: string;
  member_id: string | null;
  organization_name?: string;
  member_name?: string;
  moodle_url: string;
  enabled_by_org: boolean;
  approved_by_forbund: boolean;
  agreement_accepted: boolean;
  sync_enabled: boolean;
  last_sync_at: string | null;
  last_sync_status: string | null;
  total_synced_completions: number;
  total_certificates_issued: number;
  mapping_count?: number;
  api_token_encrypted: string | null;
}

interface TutorLMSIntegration {
  id: string;
  organization_id: string;
  member_id: string | null;
  organization_name?: string;
  member_name?: string;
  wordpress_url: string;
  enabled_by_org: boolean;
  approved_by_forbund: boolean;
  agreement_confirmed: boolean;
  sync_enabled: boolean;
  last_sync_at: string | null;
  last_sync_status: string | null;
  total_synced_completions: number;
  total_certificates_issued: number;
  mapping_count?: number;
  client_secret_encrypted: string | null;
}

interface LearnDashIntegration {
  id: string;
  organization_id: string;
  member_id: string | null;
  organization_name?: string;
  member_name?: string;
  wordpress_url: string;
  enabled_by_org: boolean;
  approved_by_forbund: boolean;
  agreement_confirmed: boolean;
  sync_enabled: boolean;
  last_sync_at: string | null;
  last_sync_status: string | null;
  total_synced_completions: number;
  total_certificates_issued: number;
  mapping_count?: number;
  client_secret_encrypted: string | null;
}

export default function IntegrationManagement() {
  const user = getUser();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [integrations, setIntegrations] = useState<MemberIntegration[]>([]);
  const [moodleIntegrations, setMoodleIntegrations] = useState<MoodleIntegration[]>([]);
  const [tutorLmsIntegrations, setTutorLmsIntegrations] = useState<TutorLMSIntegration[]>([]);
  const [learnDashIntegrations, setLearnDashIntegrations] = useState<LearnDashIntegration[]>([]);
  const [loadingMoodle, setLoadingMoodle] = useState(true);
  const [loadingTutorLms, setLoadingTutorLms] = useState(true);
  const [loadingLearnDash, setLoadingLearnDash] = useState(true);
  const [activeLms, setActiveLms] = useState<'xtramile' | 'moodle' | 'tutorlms' | 'learndash'>('xtramile');
  const [syncingId, setSyncingId] = useState<string | null>(null);

  useEffect(() => {
    loadIntegrations();
    loadMoodleIntegrations();
    loadTutorLmsIntegrations();
    loadLearnDashIntegrations();
  }, [user?.study_association_id]);

  const loadIntegrations = async () => {
    try {
      setLoading(true);
      setError('');

      let url = '/api/xtramile/integrations';

      // If admin (not systemadmin), filter by study association
      if (user?.role === 'admin' && user?.study_association_id) {
        url = `/api/xtramile/integrations/by-study-association/${user.study_association_id}`;
      }

      const response = await authenticatedFetch(url);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Kunne ikke hente integrasjoner');
      }

      setIntegrations(data.integrations || []);

    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadMoodleIntegrations = async () => {
    try {
      setLoadingMoodle(true);

      const response = await authenticatedFetch('/api/moodle/integrations');

      if (!response.ok) {
        if (response.status !== 404) {
          console.error('Failed to load Moodle integrations');
        }
        setMoodleIntegrations([]);
        return;
      }

      const data = await response.json();
      setMoodleIntegrations(Array.isArray(data) ? data : []);

    } catch (err: any) {
      console.error('Error loading Moodle integrations:', err);
      setMoodleIntegrations([]);
    } finally {
      setLoadingMoodle(false);
    }
  };

  const loadTutorLmsIntegrations = async () => {
    try {
      setLoadingTutorLms(true);

      const response = await authenticatedFetch('/api/tutorlms/integrations');

      if (!response.ok) {
        if (response.status !== 404) {
          console.error('Failed to load TutorLMS integrations');
        }
        setTutorLmsIntegrations([]);
        return;
      }

      const data = await response.json();
      setTutorLmsIntegrations(Array.isArray(data) ? data : []);

    } catch (err: any) {
      console.error('Error loading TutorLMS integrations:', err);
      setTutorLmsIntegrations([]);
    } finally {
      setLoadingTutorLms(false);
    }
  };

  const loadLearnDashIntegrations = async () => {
    try {
      setLoadingLearnDash(true);

      const response = await authenticatedFetch('/api/learndash/integrations');

      if (!response.ok) {
        if (response.status !== 404) {
          console.error('Failed to load LearnDash integrations');
        }
        setLearnDashIntegrations([]);
        return;
      }

      const data = await response.json();
      setLearnDashIntegrations(Array.isArray(data) ? data : []);

    } catch (err: any) {
      console.error('Error loading LearnDash integrations:', err);
      setLearnDashIntegrations([]);
    } finally {
      setLoadingLearnDash(false);
    }
  };

  const handleToggleMoodleApproval = async (integration: MoodleIntegration) => {
    try {
      setError('');
      setSuccess('');

      const response = await authenticatedFetch(`/api/moodle/integrations/${integration.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ approved_by_forbund: !integration.approved_by_forbund })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Kunne ikke endre status');
      }

      setSuccess(integration.approved_by_forbund ? 'Integrasjon deaktivert' : 'Integrasjon godkjent');
      await loadMoodleIntegrations();

    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleMoodleSync = async (integrationId: string) => {
    try {
      setError('');
      setSuccess('');
      setSyncingId(integrationId);

      const response = await authenticatedFetch('/api/moodle/sync', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ integration_id: integrationId })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Synkronisering feilet');
      }

      // Show completion message with minimum display time
      setSuccess('Synkronisering fullført!');
      setSyncingId(null);
      await loadMoodleIntegrations();

      // Keep success message visible for 3 seconds
      setTimeout(() => setSuccess(''), 3000);

    } catch (err: any) {
      setError(err.message);
      setSyncingId(null);
    }
  };

  const handleToggleTutorLmsApproval = async (integration: TutorLMSIntegration) => {
    try {
      setError('');
      setSuccess('');

      const response = await authenticatedFetch(`/api/tutorlms/integrations/${integration.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ approved_by_forbund: !integration.approved_by_forbund })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Kunne ikke endre status');
      }

      setSuccess(integration.approved_by_forbund ? 'Integrasjon deaktivert' : 'Integrasjon godkjent');
      await loadTutorLmsIntegrations();

    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleTutorLmsSync = async (integrationId: string) => {
    try {
      setError('');
      setSuccess('');
      setSyncingId(integrationId);

      const response = await authenticatedFetch('/api/tutorlms/sync', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ integration_id: integrationId })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Synkronisering feilet');
      }

      // Show completion message with minimum display time
      setSuccess('Synkronisering fullført!');
      setSyncingId(null);
      await loadTutorLmsIntegrations();

      // Keep success message visible for 3 seconds
      setTimeout(() => setSuccess(''), 3000);

    } catch (err: any) {
      setError(err.message);
      setSyncingId(null);
    }
  };

  const handleToggleLearnDashApproval = async (integration: LearnDashIntegration) => {
    try {
      setError('');
      setSuccess('');

      const response = await authenticatedFetch(`/api/learndash/integrations/${integration.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ approved_by_forbund: !integration.approved_by_forbund })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Kunne ikke endre status');
      }

      setSuccess(integration.approved_by_forbund ? 'Integrasjon deaktivert' : 'Integrasjon godkjent');
      await loadLearnDashIntegrations();

    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleLearnDashSync = async (integrationId: string) => {
    try {
      setError('');
      setSuccess('');
      setSyncingId(integrationId);

      const response = await authenticatedFetch('/api/learndash/sync', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ integration_id: integrationId })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Synkronisering feilet');
      }

      // Show completion message with minimum display time
      setSuccess('Synkronisering fullført!');
      setSyncingId(null);
      await loadLearnDashIntegrations();

      // Keep success message visible for 3 seconds
      setTimeout(() => setSuccess(''), 3000);

    } catch (err: any) {
      setError(err.message);
      setSyncingId(null);
    }
  };

  const handleToggleEnabled = async (integration: MemberIntegration) => {
    try {
      setError('');
      setSuccess('');

      const response = await authenticatedFetch(`/api/xtramile/integrations/${integration.id}/enable`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: !integration.approved_by_forbund })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Kunne ikke endre status');
      }

      setSuccess(data.message);
      await loadIntegrations();

    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleSync = async (integrationId: string) => {
    try {
      setError('');
      setSuccess('');
      setSyncingId(integrationId);

      const response = await authenticatedFetch('/api/xtramile/sync', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ integrationId })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Synkronisering feilet');
      }

      // Show completion message with minimum display time
      setSuccess('Synkronisering fullført!');
      setSyncingId(null);
      await loadIntegrations();

      // Keep success message visible for 3 seconds
      setTimeout(() => setSuccess(''), 3000);

    } catch (err: any) {
      setError(err.message);
      setSyncingId(null);
    }
  };

  const getStatusBadge = (integration: MemberIntegration) => {
    if (!integration.has_api_key) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
          Mangler API-nøkkel
        </span>
      );
    }

    if (!integration.agreement_confirmed) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
          <AlertCircle size={12} />
          Avtaler ikke bekreftet
        </span>
      );
    }

    if (!integration.approved_by_forbund) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-kf-blue-100 text-kf-blue-700">
          <AlertCircle size={12} />
          Venter godkjenning
        </span>
      );
    }

    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
        <CheckCircle size={12} />
        Aktiv
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="animate-spin h-8 w-8 text-kf-primary" />
      </div>
    );
  }

  const getMoodleStatusBadge = (integration: MoodleIntegration) => {
    if (!integration.api_token_encrypted) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
          Mangler API-token
        </span>
      );
    }

    if (!integration.agreement_accepted) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
          <AlertCircle size={12} />
          Avtale ikke akseptert
        </span>
      );
    }

    if (!integration.approved_by_forbund) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-kf-blue-100 text-kf-blue-700">
          <AlertCircle size={12} />
          Venter godkjenning
        </span>
      );
    }

    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
        <CheckCircle size={12} />
        Aktiv
      </span>
    );
  };

  const getTutorLmsStatusBadge = (integration: TutorLMSIntegration) => {
    if (!integration.client_secret_encrypted) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
          Mangler API-tilgang
        </span>
      );
    }

    if (!integration.agreement_confirmed) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
          <AlertCircle size={12} />
          Avtaler ikke bekreftet
        </span>
      );
    }

    if (!integration.approved_by_forbund) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-kf-blue-100 text-kf-blue-700">
          <AlertCircle size={12} />
          Venter godkjenning
        </span>
      );
    }

    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
        <CheckCircle size={12} />
        Aktiv
      </span>
    );
  };

  const getLearnDashStatusBadge = (integration: LearnDashIntegration) => {
    if (!integration.client_secret_encrypted) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
          Mangler API-tilgang
        </span>
      );
    }

    if (!integration.agreement_confirmed) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
          <AlertCircle size={12} />
          Avtaler ikke bekreftet
        </span>
      );
    }

    if (!integration.approved_by_forbund) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-kf-blue-100 text-kf-blue-700">
          <AlertCircle size={12} />
          Venter godkjenning
        </span>
      );
    }

    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
        <CheckCircle size={12} />
        Aktiv
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* LMS Tabs */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="border-b">
          <nav className="flex -mb-px">
            <button
              onClick={() => setActiveLms('xtramile')}
              className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                activeLms === 'xtramile'
                  ? 'border-kf-blue-600 text-kf-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Link2 size={18} />
              Xtramile LMS
              {integrations.length > 0 && (
                <span className="px-2 py-0.5 text-xs bg-kf-blue-100 text-kf-blue-700 rounded-full">
                  {integrations.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveLms('moodle')}
              className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                activeLms === 'moodle'
                  ? 'border-kf-orange-600 text-kf-orange-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <GraduationCap size={18} />
              Moodle LMS
              {moodleIntegrations.length > 0 && (
                <span className="px-2 py-0.5 text-xs bg-kf-orange-100 text-kf-orange-700 rounded-full">
                  {moodleIntegrations.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveLms('tutorlms')}
              className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                activeLms === 'tutorlms'
                  ? 'border-purple-600 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <GraduationCap size={18} />
              TutorLMS
              {tutorLmsIntegrations.length > 0 && (
                <span className="px-2 py-0.5 text-xs bg-purple-100 text-purple-700 rounded-full">
                  {tutorLmsIntegrations.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveLms('learndash')}
              className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                activeLms === 'learndash'
                  ? 'border-green-600 text-green-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <GraduationCap size={18} />
              LearnDash
              {learnDashIntegrations.length > 0 && (
                <span className="px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded-full">
                  {learnDashIntegrations.length}
                </span>
              )}
            </button>
          </nav>
        </div>
      </div>

      <div className="flex items-center justify-end">
        <button
          onClick={() => {
            if (activeLms === 'xtramile') {
              loadIntegrations();
            } else if (activeLms === 'moodle') {
              loadMoodleIntegrations();
            } else {
              loadTutorLmsIntegrations();
            }
          }}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          <RefreshCw size={16} />
          Oppdater
        </button>
      </div>

      {/* Error/Success messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <XCircle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={20} />
            <p className="text-sm text-green-700">{success}</p>
          </div>
        </div>
      )}

      {/* Moodle Section */}
      {activeLms === 'moodle' && (
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="p-6 border-b">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-kf-orange-100 rounded-lg">
                <GraduationCap className="text-kf-orange-600" size={24} />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Moodle LMS</h2>
                <p className="text-sm text-gray-500">
                  {moodleIntegrations.length} {moodleIntegrations.length === 1 ? 'integrasjon' : 'integrasjoner'} konfigurert
                </p>
              </div>
            </div>
          </div>

          {loadingMoodle ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="animate-spin h-8 w-8 text-kf-orange-600" />
            </div>
          ) : moodleIntegrations.length === 0 ? (
            <div className="p-8 text-center">
              <Building2 className="mx-auto h-12 w-12 text-gray-300" />
              <p className="mt-4 text-gray-500">Ingen Moodle-integrasjoner konfigurert ennå</p>
              <p className="mt-2 text-sm text-gray-400">
                Medlemsorganisasjoner kan sette opp Moodle-integrasjon under sine innstillinger
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Medlemsorganisasjon
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Moodle URL
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Statistikk
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Handlinger
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {moodleIntegrations.map((integration) => (
                    <tr key={integration.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <Building2 className="text-gray-400" size={20} />
                          <span className="font-medium text-gray-900">
                            {integration.member_name || integration.organization_name || 'Ukjent'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <a
                          href={integration.moodle_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-kf-blue-600 hover:underline text-sm"
                        >
                          {integration.moodle_url}
                        </a>
                      </td>
                      <td className="px-6 py-4">
                        {getMoodleStatusBadge(integration)}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm">
                          <p className="text-gray-900">{integration.total_synced_completions} fullføringer</p>
                          <p className="text-gray-500">{integration.total_certificates_issued} attester</p>
                          {integration.last_sync_at && (
                            <p className="text-xs text-gray-400 mt-1">
                              Sist synk: {new Date(integration.last_sync_at).toLocaleDateString('nb-NO')}
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex justify-end gap-2">
                          {integration.api_token_encrypted && integration.agreement_accepted && (
                            <>
                              <button
                                onClick={() => handleMoodleSync(integration.id)}
                                disabled={syncingId === integration.id}
                                className={`px-3 py-1 text-xs font-medium rounded flex items-center gap-1 ${
                                  syncingId === integration.id
                                    ? 'text-kf-orange-500 bg-kf-orange-50 cursor-wait'
                                    : 'text-kf-orange-700 bg-kf-orange-100 hover:bg-kf-orange-200'
                                }`}
                              >
                                {syncingId === integration.id ? (
                                  <>
                                    <Loader2 size={12} className="animate-spin" />
                                    Synkroniserer...
                                  </>
                                ) : (
                                  'Synk nå'
                                )}
                              </button>
                              <button
                                onClick={() => handleToggleMoodleApproval(integration)}
                                className={`px-3 py-1 text-xs font-medium rounded ${
                                  integration.approved_by_forbund
                                    ? 'text-red-700 bg-red-100 hover:bg-red-200'
                                    : 'text-green-700 bg-green-100 hover:bg-green-200'
                                }`}
                              >
                                {integration.approved_by_forbund ? 'Deaktiver' : 'Godkjenn'}
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Xtramile Section */}
      {activeLms === 'xtramile' && (
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="p-6 border-b">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-kf-blue-100 rounded-lg">
              <Link2 className="text-kf-blue-600" size={24} />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Xtramile LMS</h2>
              <p className="text-sm text-gray-500">
                {integrations.length} {integrations.length === 1 ? 'integrasjon' : 'integrasjoner'} konfigurert
              </p>
            </div>
          </div>
        </div>

        {integrations.length === 0 ? (
          <div className="p-8 text-center">
            <Building2 className="mx-auto h-12 w-12 text-gray-300" />
            <p className="mt-4 text-gray-500">Ingen integrasjoner konfigurert ennå</p>
            <p className="mt-2 text-sm text-gray-400">
              Medlemsorganisasjoner kan sette opp integrasjon under sine innstillinger
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Medlemsorganisasjon
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Bekreftelser
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statistikk
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Handlinger
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {integrations.map((integration) => (
                  <tr key={integration.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <Building2 className="text-gray-400" size={20} />
                        <span className="font-medium text-gray-900">{integration.member_name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {getStatusBadge(integration)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col gap-1 text-xs">
                        <span className={integration.quality_control_confirmed ? 'text-green-600' : 'text-gray-400'}>
                          {integration.quality_control_confirmed ? '✓' : '○'} Kvalitetskontroll
                        </span>
                        <span className={integration.content_alignment_confirmed ? 'text-green-600' : 'text-gray-400'}>
                          {integration.content_alignment_confirmed ? '✓' : '○'} Innholdssamsvar
                        </span>
                        <span className={integration.spot_check_allowed ? 'text-green-600' : 'text-gray-400'}>
                          {integration.spot_check_allowed ? '✓' : '○'} Stikkprøver
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm">
                        <p className="text-gray-900">{integration.total_synced_completions} fullføringer</p>
                        <p className="text-gray-500">{integration.total_certificates_issued} attester</p>
                        {integration.last_sync_at && (
                          <p className="text-xs text-gray-400 mt-1">
                            Sist synk: {new Date(integration.last_sync_at).toLocaleDateString('nb-NO')}
                          </p>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex justify-end gap-2">
                        {integration.has_api_key && integration.agreement_confirmed && (
                          <>
                            <button
                              onClick={() => handleSync(integration.id)}
                              disabled={syncingId === integration.id}
                              className={`px-3 py-1 text-xs font-medium rounded flex items-center gap-1 ${
                                syncingId === integration.id
                                  ? 'text-kf-blue-500 bg-kf-blue-50 cursor-wait'
                                  : 'text-kf-blue-700 bg-kf-blue-100 hover:bg-kf-blue-200'
                              }`}
                            >
                              {syncingId === integration.id ? (
                                <>
                                  <Loader2 size={12} className="animate-spin" />
                                  Synkroniserer...
                                </>
                              ) : (
                                'Synk nå'
                              )}
                            </button>
                            <button
                              onClick={() => handleToggleEnabled(integration)}
                              className={`px-3 py-1 text-xs font-medium rounded ${
                                integration.approved_by_forbund
                                  ? 'text-red-700 bg-red-100 hover:bg-red-200'
                                  : 'text-green-700 bg-green-100 hover:bg-green-200'
                              }`}
                            >
                              {integration.approved_by_forbund ? 'Deaktiver' : 'Aktiver'}
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      )}

      {/* Info box */}
      {activeLms === 'xtramile' && (
      <div className="bg-kf-blue-50 border border-kf-blue-200 rounded-lg p-4">
        <h3 className="font-medium text-kf-blue-800">Om Xtramile-integrasjon</h3>
        <p className="mt-2 text-sm text-kf-blue-700">
          Xtramile LMS-integrasjonen lar medlemsorganisasjoner automatisk synkronisere kursgjennomføringer
          og utstede kompetanseattester basert på fullførte kurs i Xtramile. Hver organisasjon må bekrefte
          avtaler om databehandling, kvalitetskontroll og stikkprøvekontroll før integrasjonen aktiveres.
        </p>
      </div>
      )}

      {activeLms === 'moodle' && (
      <div className="bg-kf-orange-50 border border-kf-orange-200 rounded-lg p-4">
        <h3 className="font-medium text-kf-orange-800">Om Moodle-integrasjon</h3>
        <p className="mt-2 text-sm text-kf-orange-700">
          Moodle LMS-integrasjonen lar medlemsorganisasjoner automatisk synkronisere kursgjennomføringer
          fra Moodle og utstede kompetanseattester. Kandidater matches ved både e-post og mobilnummer
          for sikker identifisering. Kurs sjekkes daglig og mappinger deaktiveres automatisk etter 3 dager
          hvis kurset ikke finnes i Moodle.
        </p>
      </div>
      )}

      {/* TutorLMS Section */}
      {activeLms === 'tutorlms' && (
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="p-6 border-b">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <GraduationCap className="text-purple-600" size={24} />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">TutorLMS</h2>
                <p className="text-sm text-gray-500">
                  {tutorLmsIntegrations.length} {tutorLmsIntegrations.length === 1 ? 'integrasjon' : 'integrasjoner'} konfigurert
                </p>
              </div>
            </div>
          </div>

          {loadingTutorLms ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="animate-spin h-8 w-8 text-purple-600" />
            </div>
          ) : tutorLmsIntegrations.length === 0 ? (
            <div className="p-8 text-center">
              <Building2 className="mx-auto h-12 w-12 text-gray-300" />
              <p className="mt-4 text-gray-500">Ingen TutorLMS-integrasjoner konfigurert ennå</p>
              <p className="mt-2 text-sm text-gray-400">
                Medlemsorganisasjoner kan sette opp TutorLMS-integrasjon under sine innstillinger
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Medlemsorganisasjon
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      WordPress URL
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Statistikk
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Handlinger
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {tutorLmsIntegrations.map((integration) => (
                    <tr key={integration.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <Building2 className="text-gray-400" size={20} />
                          <span className="font-medium text-gray-900">
                            {integration.member_name || integration.organization_name || 'Ukjent'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <a
                          href={integration.wordpress_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-kf-blue-600 hover:underline text-sm"
                        >
                          {integration.wordpress_url}
                        </a>
                      </td>
                      <td className="px-6 py-4">
                        {getTutorLmsStatusBadge(integration)}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm">
                          <p className="text-gray-900">{integration.total_synced_completions} fullføringer</p>
                          <p className="text-gray-500">{integration.total_certificates_issued} attester</p>
                          {integration.last_sync_at && (
                            <p className="text-xs text-gray-400 mt-1">
                              Sist synk: {new Date(integration.last_sync_at).toLocaleDateString('nb-NO')}
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex justify-end gap-2">
                          {integration.client_secret_encrypted && integration.agreement_confirmed && (
                            <>
                              <button
                                onClick={() => handleTutorLmsSync(integration.id)}
                                disabled={syncingId === integration.id}
                                className={`px-3 py-1 text-xs font-medium rounded flex items-center gap-1 ${
                                  syncingId === integration.id
                                    ? 'text-purple-500 bg-purple-50 cursor-wait'
                                    : 'text-purple-700 bg-purple-100 hover:bg-purple-200'
                                }`}
                              >
                                {syncingId === integration.id ? (
                                  <>
                                    <Loader2 size={12} className="animate-spin" />
                                    Synkroniserer...
                                  </>
                                ) : (
                                  'Synk nå'
                                )}
                              </button>
                              <button
                                onClick={() => handleToggleTutorLmsApproval(integration)}
                                className={`px-3 py-1 text-xs font-medium rounded ${
                                  integration.approved_by_forbund
                                    ? 'text-red-700 bg-red-100 hover:bg-red-200'
                                    : 'text-green-700 bg-green-100 hover:bg-green-200'
                                }`}
                              >
                                {integration.approved_by_forbund ? 'Deaktiver' : 'Godkjenn'}
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeLms === 'tutorlms' && (
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
        <h3 className="font-medium text-purple-800">Om TutorLMS-integrasjon</h3>
        <p className="mt-2 text-sm text-purple-700">
          TutorLMS-integrasjonen lar medlemsorganisasjoner automatisk synkronisere kursgjennomføringer
          fra WordPress/TutorLMS og utstede kompetanseattester. Integrasjonen krever TutorLMS Pro for
          tilgang til fullføringsdata via API. Kandidater verifiseres med Vipps før attest utstedes.
        </p>
      </div>
      )}

      {/* LearnDash Section */}
      {activeLms === 'learndash' && (
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="p-6 border-b">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <GraduationCap className="text-green-600" size={24} />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">LearnDash</h2>
                <p className="text-sm text-gray-500">
                  {learnDashIntegrations.length} {learnDashIntegrations.length === 1 ? 'integrasjon' : 'integrasjoner'} konfigurert
                </p>
              </div>
            </div>
          </div>

          {loadingLearnDash ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="animate-spin h-8 w-8 text-green-600" />
            </div>
          ) : learnDashIntegrations.length === 0 ? (
            <div className="p-8 text-center">
              <Building2 className="mx-auto h-12 w-12 text-gray-300" />
              <p className="mt-4 text-gray-500">Ingen LearnDash-integrasjoner konfigurert ennå</p>
              <p className="mt-2 text-sm text-gray-400">
                Medlemsorganisasjoner kan sette opp LearnDash-integrasjon under sine innstillinger
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Medlemsorganisasjon
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      WordPress URL
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Statistikk
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Handlinger
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {learnDashIntegrations.map((integration) => (
                    <tr key={integration.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <Building2 className="text-gray-400" size={20} />
                          <span className="font-medium text-gray-900">
                            {integration.member_name || integration.organization_name || 'Ukjent'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <a
                          href={integration.wordpress_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-kf-blue-600 hover:underline text-sm"
                        >
                          {integration.wordpress_url}
                        </a>
                      </td>
                      <td className="px-6 py-4">
                        {getLearnDashStatusBadge(integration)}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm">
                          <p className="text-gray-900">{integration.total_synced_completions} fullføringer</p>
                          <p className="text-gray-500">{integration.total_certificates_issued} attester</p>
                          {integration.last_sync_at && (
                            <p className="text-xs text-gray-400 mt-1">
                              Sist synk: {new Date(integration.last_sync_at).toLocaleDateString('nb-NO')}
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex justify-end gap-2">
                          {integration.client_secret_encrypted && integration.agreement_confirmed && (
                            <>
                              <button
                                onClick={() => handleLearnDashSync(integration.id)}
                                disabled={syncingId === integration.id}
                                className={`px-3 py-1 text-xs font-medium rounded flex items-center gap-1 ${
                                  syncingId === integration.id
                                    ? 'text-green-500 bg-green-50 cursor-wait'
                                    : 'text-green-700 bg-green-100 hover:bg-green-200'
                                }`}
                              >
                                {syncingId === integration.id ? (
                                  <>
                                    <Loader2 size={12} className="animate-spin" />
                                    Synkroniserer...
                                  </>
                                ) : (
                                  'Synk nå'
                                )}
                              </button>
                              <button
                                onClick={() => handleToggleLearnDashApproval(integration)}
                                className={`px-3 py-1 text-xs font-medium rounded ${
                                  integration.approved_by_forbund
                                    ? 'text-red-700 bg-red-100 hover:bg-red-200'
                                    : 'text-green-700 bg-green-100 hover:bg-green-200'
                                }`}
                              >
                                {integration.approved_by_forbund ? 'Deaktiver' : 'Godkjenn'}
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeLms === 'learndash' && (
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <h3 className="font-medium text-green-800">Om LearnDash-integrasjon</h3>
        <p className="mt-2 text-sm text-green-700">
          LearnDash-integrasjonen lar medlemsorganisasjoner automatisk synkronisere kursgjennomføringer
          fra WordPress/LearnDash og utstede kompetanseattester. REST API v2 er tilgjengelig i alle
          lisensplaner. Kandidater verifiseres med Vipps før attest utstedes.
        </p>
      </div>
      )}
    </div>
  );
}
